/**
 * Tarneeb bot.
 *
 * - Bidding: for each suit as a candidate trump, estimates the tricks the hand takes alone (trump length and honours,
 *   side-suit aces/kings, ruffing potential), adds an allowance for the partner, and bids only when that reaches the
 *   minimum; it will not outbid a partner who already holds the bid unless it is clearly stronger.
 * - Trump: the suit with the best estimate.
 * - Play: partnership-aware heuristic. Every trick scores for one of the two teams, so it takes tricks as cheaply as
 *   possible, lets a winning partner keep the trick, ruffs when void, and leads master cards. NORMAL and above track
 *   played cards to recognise masters.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  tarneebBestSuit,
  tarneebPartnerOf,
  tarneebTrickWinner,
  type TarneebAction,
  type TarneebLegal,
  type TarneebTrickPlay,
  type TarneebView,
} from './engine.js';

const ESTIMATE_SCALE = 1.5;
/** Tricks a partner is assumed to contribute on average when deciding whether the team can make a bid. */
const PARTNER_ALLOWANCE = 0.8;

/** Tricks the hand is expected to take alone with `trump` as trump. */
export function estimateTarneebTricks(hand: readonly CardId[], trump: Suit): number {
  const cards = hand.map(parseCard);
  const bySuit = new Map<Suit, number[]>();
  for (const s of SUITS) bySuit.set(s, []);
  for (const c of cards) bySuit.get(c.suit)!.push(c.rank);
  for (const ranks of bySuit.values()) ranks.sort((a, b) => b - a);
  const trumps = bySuit.get(trump)!;
  const tLen = trumps.length;
  let est = 0;
  for (const [suit, ranks] of bySuit) {
    if (suit === trump) continue;
    const len = ranks.length;
    if (ranks.includes(14)) est += len >= 7 ? 0.7 : 1;
    if (ranks.includes(13)) est += len >= 2 && len <= 4 ? (ranks.includes(14) ? 0.8 : 0.5) : 0.1;
  }
  for (const r of trumps) {
    if (r === 14) est += 1;
    else if (r === 13) est += tLen >= 2 ? 0.9 : 0.5;
    else if (r === 12) est += tLen >= 3 ? 0.7 : 0.3;
    else if (r === 11) est += tLen >= 4 ? 0.45 : 0.1;
  }
  if (tLen > 4) est += (tLen - 4) * 0.8;
  const spare = Math.max(0, tLen - trumps.filter((r) => r >= 12).length);
  let ruffs = 0;
  for (const [suit, ranks] of bySuit) {
    if (suit === trump) continue;
    if (ranks.length === 0) ruffs += 1;
    else if (ranks.length === 1) ruffs += 0.5;
  }
  est += Math.min(ruffs, spare) * 0.6;
  return est * ESTIMATE_SCALE;
}

function bestTrump(hand: readonly CardId[]): { suit: Suit; est: number } {
  let suit = tarneebBestSuit(hand);
  let est = estimateTarneebTricks(hand, suit);
  for (const s of SUITS) {
    const e = estimateTarneebTricks(hand, s);
    if (e > est + 0.05) {
      est = e;
      suit = s;
    }
  }
  return { suit, est };
}

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: TarneebView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => !seen.has(c))) };
}

function isMaster(card: CardId, k: Knowledge): boolean {
  const c = parseCard(card);
  for (const u of k.unseen) {
    const x = parseCard(u);
    if (x.suit === c.suit && x.rank > c.rank) return false;
  }
  return true;
}

function wouldWin(trick: readonly TarneebTrickPlay[], seat: number, card: CardId, trump: Suit): boolean {
  if (trick.length === 0) return true;
  return tarneebTrickWinner([...trick, { seat, card }], trump) === seat;
}

function chooseCard(view: TarneebView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const me = view.seat!;
  const partner = tarneebPartnerOf(me);
  const trump = view.trump!;
  const trick = view.trick;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  const power = (id: CardId) => {
    const c = parseCard(id);
    return (c.suit === trump ? 100 : 0) + c.rank;
  };
  const lowest = (cards: CardId[]) => [...cards].sort((a, b) => power(a) - power(b))[0]!;
  const highest = (cards: CardId[]) => [...cards].sort((a, b) => power(b) - power(a))[0]!;
  const dump = (cards: CardId[]) => {
    const side = cards.filter((c) => parseCard(c).suit !== trump);
    if (side.length === 0) return lowest(cards);
    // Shed from the suit held longest, lowest first — keeps short suits for ruffing.
    const counts = new Map<Suit, number>();
    side.forEach((c) => counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1));
    const longest = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]![0];
    return lowest(side.filter((c) => parseCard(c).suit === longest));
  };

  if (trick.length === 0) {
    // Leading: a master side-suit card, then trumps if we hold a strong trump suit, else a low card from the shortest side suit.
    const side = legal.filter((c) => parseCard(c).suit !== trump);
    const masters = k ? side.filter((c) => isMaster(c, k)) : side.filter((c) => parseCard(c).rank === 14);
    if (masters.length) return highest(masters);
    const trumps = legal.filter((c) => parseCard(c).suit === trump);
    if (k && trumps.length >= 4) {
      const top = trumps.filter((c) => isMaster(c, k));
      if (top.length) return highest(top);
    }
    if (side.length) {
      const counts = new Map<Suit, number>();
      side.forEach((c) => counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1));
      const shortSuit = [...counts.entries()].sort((a, b) => a[1] - b[1])[0]![0];
      return lowest(side.filter((c) => parseCard(c).suit === shortSuit));
    }
    return lowest(legal);
  }

  const winnerSeat = tarneebTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === 3;
  if (winnerSeat === partner) {
    // Partner is winning: keep our winners unless a later opponent could still overtake a non-master card.
    if (last || !k || isMaster(winnerCard, k)) return dump(legal);
    const over = legal.filter((c) => wouldWin(trick, me, c, trump) && parseCard(c).suit === parseCard(winnerCard).suit);
    if (over.length && parseCard(winnerCard).rank < 10) return lowest(over);
    return dump(legal);
  }
  const winners = legal.filter((c) => wouldWin(trick, me, c, trump));
  if (winners.length === 0) return dump(legal);
  if (last) return lowest(winners);
  const safe = k ? winners.filter((c) => isMaster(c, k) || parseCard(c).suit === trump) : winners;
  return lowest(safe.length ? safe : winners);
}

export const tarneebBot: BotEngine<TarneebView, TarneebLegal, TarneebAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'trump') return { type: 'trump', suit: bestTrump(view.hand).suit };
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    const me = view.seat!;
    let est = bestTrump(view.hand).est + PARTNER_ALLOWANCE;
    if (ctx.difficulty === 'BEGINNER') est += (ctx.rng.next() - 0.5) * 2.5;
    else if (ctx.difficulty === 'EASY') est += (ctx.rng.next() - 0.5) * 1.5;
    const shade = ctx.difficulty === 'HARD' || ctx.difficulty === 'EXPERT' ? 0.2 : 0.05;
    let value = Math.min(13, Math.round(est - shade));
    // Leave the bid with a partner who already holds it unless we are clearly stronger.
    if (view.highBid?.seat === tarneebPartnerOf(me) && value < legal.min + 1) value = 0;
    if (value >= legal.min) return { type: 'bid', value };
    return legal.canPass ? { type: 'pass' } : { type: 'bid', value: legal.min };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 40));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') return Math.min(1, Math.max(0, (legal.min - bestTrump(view.hand).est) / 13));
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return legal.canPass ? { type: 'pass' } : { type: 'bid', value: legal.min };
    if (legal.kind === 'trump') return { type: 'trump', suit: bestTrump(view.hand).suit };
    if (legal.kind === 'play') {
      const trump = view.trump;
      const power = (id: CardId) => (parseCard(id).suit === trump ? 100 : 0) + parseCard(id).rank;
      return { type: 'play', card: [...legal.cards].sort((a, b) => power(a) - power(b))[0]! };
    }
    throw new Error('No legal action');
  },
};
